import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { AvaliacaoService, RegistroAvaliacao, ItemAvaliavel } from '../../../core/config/services/avaliacao.service';
import {
  NotaView,
  ItemEducacional,
  Periodo,
  Encerramento,
  EnturmacaoCompleta,
  CalculoFormula,
  LinhaDesempenho,
  ColunaDesempenho
} from '../models/nota-types';
import { NotaTransformerService } from './nota-transformer.service';
import { NotaViewBuilderService } from './nota-view-builder.service';

// Re-exportar tipos para manter compatibilidade com código existente
export type {
  NotaView,
  ItemEducacional,
  Periodo,
  Encerramento,
  EnturmacaoCompleta,
  CalculoFormula,
  LinhaDesempenho,
  ColunaDesempenho
};

@Injectable({
  providedIn: 'root'
})
export class NotaService {
  private apiUrl = '/api/avaliacao';

  constructor(
    private avaliacaoService: AvaliacaoService,
    private http: HttpClient,
    private notaTransformer: NotaTransformerService,
    private notaViewBuilder: NotaViewBuilderService
  ) { }

  /** Estrutura baseada no DesempenhoEscolarViewService do AngularJS. */
  createView(
    registros: { avaliacoes: { itensAvaliaveis: ItemAvaliavel[]; registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[]; calculo?: CalculoFormula }; encerramentos?: Encerramento[] },
    itemEducacional: ItemEducacional,
    periodo: Periodo | null,
    enturmacoes: EnturmacaoCompleta[],
    encerramentosTurma: Encerramento[],
    porInstrumento = true
  ): { colunas: ColunaDesempenho[]; linhas: LinhaDesempenho[]; colunaMediaPeriodo?: ColunaDesempenho | null; calculoFormula?: CalculoFormula | null } {
    return this.notaViewBuilder.createView(registros, itemEducacional, periodo, enturmacoes, encerramentosTurma, porInstrumento);
  }

  /** Estrutura baseada no DesempenhoEscolarYearViewService do AngularJS. */
  createYearView(
    registros: { avaliacoes: { itensAvaliaveis: ItemAvaliavel[]; registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[]; calculo?: CalculoFormula }; abonos?: unknown[] },
    itemEducacional: ItemEducacional,
    itensAvaliaveis: ItemAvaliavel[],
    periodos: Periodo[],
    enturmacoes: RegistroAvaliacao['enturmacao'][]
  ): { colunas: ColunaDesempenho[]; linhas: LinhaDesempenho[]; colunaMediaFinal?: ColunaDesempenho | null } {
    return this.notaViewBuilder.createYearView(registros, itemEducacional, itensAvaliaveis, periodos, enturmacoes);
  }

  private updateNotaFields(nota: NotaView): void {
    nota.$$nota = this.notaTransformer.getNotaValue(nota as RegistroAvaliacao);
  }

  salvarNota(turmaId: number, nota: NotaView, context?: HttpContext): Observable<RegistroAvaliacao> {
    const registro = this.buildRegistroForSave(nota);

    return this.avaliacaoService.salvar(turmaId, registro as unknown as RegistroAvaliacao, context);
  }

  private buildRegistroForSave(nota: NotaView): Record<string, unknown> {
    const notaRecord = nota as unknown as Record<string, unknown>;
    const payloadBase: Record<string, unknown> = {
      ...nota,
      notaNumerica: this.getNotaNumericaForSave(nota.notaNumerica),
      notaDescritiva: this.getNotaDescritivaForSave(nota.notaDescritiva),
      notaConceito: this.getNotaConceitoForSave(nota.notaConceito),
      filhos: this.getArrayForSave(notaRecord['filhos']),
      arquivos: this.getArrayForSave(notaRecord['arquivos'])
    };

    return this.sanitizePayloadForSave(payloadBase) as Record<string, unknown>;
  }

  private getNotaNumericaForSave(notaNumerica: number | undefined | null): number | null {
    return notaNumerica !== null && notaNumerica !== undefined ? notaNumerica : null;
  }

  private getNotaDescritivaForSave(notaDescritiva: string | undefined | null): string | null {
    return notaDescritiva !== null && notaDescritiva !== undefined && notaDescritiva !== '' 
      ? notaDescritiva 
      : null;
  }

  private getNotaConceitoForSave(notaConceito: NotaView['notaConceito'] | null | undefined): NotaView['notaConceito'] | null {
    const valorNormalizado = this.normalizeNotaConceito(notaConceito);

    return valorNormalizado !== null
      ? valorNormalizado
      : null;
  }

  private normalizeNotaConceito(notaConceito: NotaView['notaConceito'] | null | undefined): NotaView['notaConceito'] | null {
    if (typeof notaConceito === 'string') {
      return notaConceito || null;
    }

    if (notaConceito && typeof notaConceito === 'object') {
      return { ...notaConceito };
    }

    return null;
  }

  private getArrayForSave(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private sanitizePayloadForSave(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.sanitizePayloadForSave(item))
        .filter((item) => item !== undefined);
    }

    if (value && typeof value === 'object') {
      const source = value as Record<string, unknown>;
      const cleaned: Record<string, unknown> = {};

      Object.keys(source).forEach((key) => {
        if (key.startsWith('$$')) {
          return;
        }

        const currentValue = source[key];
        if (typeof currentValue === 'function' || currentValue === undefined) {
          return;
        }

        cleaned[key] = this.sanitizePayloadForSave(currentValue);
      });

      return cleaned;
    }

    return value;
  }

  limparCamposNota(nota: NotaView): void {
    const modoAvaliacao = nota.avaliavel.modoAvaliacao;
    const camposParaLimpar = this.getCamposParaLimpar(modoAvaliacao);

    camposParaLimpar.forEach(campo => {
      this.limparCampoNota(nota, campo);
    });

    if (!nota.id) {
      delete nota.id;
    }

    this.updateNotaFields(nota);
  }

  private readonly CAMPOS_POR_MODO: Record<string, Array<'notaNumerica' | 'notaDescritiva' | 'notaConceito'>> = {
    'DESCRITIVO': ['notaNumerica', 'notaConceito'],
    'NUMERICO': ['notaDescritiva', 'notaConceito'],
    'CONCEITO': ['notaNumerica', 'notaDescritiva']
  };

  private getCamposParaLimpar(modoAvaliacao?: string): Array<'notaNumerica' | 'notaDescritiva' | 'notaConceito'> {
    return modoAvaliacao ? (this.CAMPOS_POR_MODO[modoAvaliacao] || []) : [];
  }

  private limparCampoNota(nota: NotaView, campo: 'notaNumerica' | 'notaDescritiva' | 'notaConceito'): void {
    switch (campo) {
      case 'notaNumerica':
        nota.notaNumerica = undefined;
        break;
      case 'notaDescritiva':
        nota.notaDescritiva = undefined;
        break;
      case 'notaConceito':
        nota.notaConceito = undefined;
        break;
    }
  }

  // Métodos para visualização anual
  getLinhasDesempenhoAno(turma: { id: number }, periodo: { id: number }, componente: { id: number }): Observable<LinhaDesempenho[]> {
    return this.http.get<LinhaDesempenho[]>(`${this.apiUrl}/desempenho/ano/linhas`, {
      params: {
        turmaId: turma.id.toString(),
        periodoId: periodo.id.toString(),
        componenteId: componente.id.toString()
      }
    });
  }

  getColunasDesempenhoAno(turma: { id: number }, periodo: { id: number }, componente: { id: number }): Observable<ColunaDesempenho[]> {
    return this.http.get<ColunaDesempenho[]>(`${this.apiUrl}/desempenho/ano/colunas`, {
      params: {
        turmaId: turma.id.toString(),
        periodoId: periodo.id.toString(),
        componenteId: componente.id.toString()
      }
    });
  }

  // Métodos de busca e filtro
  searchLinhasDesempenho(searchText: string, linhas: LinhaDesempenho[]): Observable<LinhaDesempenho[]> {
    const searchLower = searchText.toLowerCase();
    const filtered = linhas.filter(linha =>
      (linha.enturmacao.aluno?.nome.toLowerCase().includes(searchLower) ?? false) ||
      (linha.enturmacao.aluno?.matricula.toLowerCase().includes(searchLower) ?? false) ||
      (linha.enturmacao.matricula.nomeSocial || '').toLowerCase().includes(searchLower) ||
      (linha.enturmacao.matricula.nome || '').toLowerCase().includes(searchLower)
    );
    return of(filtered);
  }

  filterLinhasDesempenho(filters: Record<string, unknown>, linhas: LinhaDesempenho[]): Observable<LinhaDesempenho[]> {
    let filtered = linhas;

    if (filters['situacaoMatricula']) {
      filtered = filtered.filter(linha => linha.enturmacao.situacaoMatricula === filters['situacaoMatricula']);
    }

    if (filters['grupoEnturmacao']) {
      filtered = filtered.filter(linha => linha.enturmacao.grupoEnturmacao === filters['grupoEnturmacao']);
    }

    if (filters['situacaoItemEducacional']) {
      filtered = filtered.filter(linha => linha.enturmacao.situacaoItemEducacional === filters['situacaoItemEducacional']);
    }

    return of(filtered);
  }
}